import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Download, FileSpreadsheet, Users, User, Search, 
  ChevronDown, ChevronUp, Filter, Loader2, BarChart3,
  Building2, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BellCurveSVG } from '../components/BellCurveSVG';
import { cn, getPerformanceScoreBg } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FULL_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Gaussian function for smooth bell
// (BellCurveSVG component extracted to /app/frontend/src/components/BellCurveSVG.jsx)


const PeriodScores = ({ defaultPeriod = 6 }) => {
  const { isDark } = useTheme();
  const now = new Date();

  // Default date range based on period
  const getDefaultRange = (period) => {
    const end = { year: now.getFullYear(), month: now.getMonth() + 1 };
    const startDate = new Date(now.getFullYear(), now.getMonth() - period + 1, 1);
    const start = { year: startDate.getFullYear(), month: startDate.getMonth() + 1 };
    return { start, end };
  };

  const defaultRange = getDefaultRange(defaultPeriod);
  const [startYear, setStartYear] = useState(String(defaultRange.start.year));
  const [startMonth, setStartMonth] = useState(String(defaultRange.start.month));
  const [endYear, setEndYear] = useState(String(defaultRange.end.year));
  const [endMonth, setEndMonth] = useState(String(defaultRange.end.month));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [sortBy, setSortBy] = useState('avg_ranking');
  const [sortDir, setSortDir] = useState('desc');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all');

  // Bell Curve state
  const [bellCurveSubTeam, setBellCurveSubTeam] = useState('all');
  const [bellCurveData, setBellCurveData] = useState(null);
  const [bellCurveLoading, setBellCurveLoading] = useState(false);
  const [downloadingBellCurve, setDownloadingBellCurve] = useState(false);

  const token = localStorage.getItem('arrise_token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/reports/period-scores`, {
        params: {
          start_year: parseInt(startYear),
          start_month: parseInt(startMonth),
          end_year: parseInt(endYear),
          end_month: parseInt(endMonth)
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch period scores:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startYear, startMonth, endYear, endMonth]);

  // Reset range when period changes
  useEffect(() => {
    const range = getDefaultRange(defaultPeriod);
    setStartYear(String(range.start.year));
    setStartMonth(String(range.start.month));
    setEndYear(String(range.end.year));
    setEndMonth(String(range.end.month));
  }, [defaultPeriod]);

  // Sub-teams for bell curve filter
  const bellCurveSubTeams = useMemo(() => {
    const subs = new Set(data.map(d => d.sub_team).filter(Boolean));
    return ['all', ...Array.from(subs).sort()];
  }, [data]);

  // Sub-team breakdown counts
  const subTeamBreakdown = useMemo(() => {
    const counts = {};
    data.forEach(d => {
      const s = d.sub_team || 'Unassigned';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [data]);

  // Locations
  const locations = useMemo(() => {
    const locs = new Set(data.map(d => d.location).filter(Boolean));
    return ['all', ...Array.from(locs).sort()];
  }, [data]);

  // Teams for table filter (sub-teams)
  const allTeams = useMemo(() => {
    const t = new Set(data.map(d => d.sub_team).filter(Boolean));
    return ['all', ...Array.from(t).sort()];
  }, [data]);

  // Filtered + sorted data for the members table
  const filtered = useMemo(() => {
    let result = data;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d => d.name.toLowerCase().includes(term) || (d.employee_id || '').toLowerCase().includes(term));
    }
    if (selectedTeam !== 'all') {
      result = result.filter(d => d.sub_team === selectedTeam || d.vertical_team === selectedTeam);
    }
    if (locationFilter !== 'all') {
      result = result.filter(d => d.location === locationFilter);
    }
    result = [...result].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return result;
  }, [data, searchTerm, selectedTeam, locationFilter, sortBy, sortDir]);

  // Bell Curve fetch
  const fetchBellCurve = useCallback(async () => {
    setBellCurveLoading(true);
    try {
      const params = {
        start_year: parseInt(startYear),
        start_month: parseInt(startMonth),
        end_year: parseInt(endYear),
        end_month: parseInt(endMonth)
      };
      if (bellCurveSubTeam !== 'all') params.sub_team = bellCurveSubTeam;
      const res = await axios.get(`${BACKEND_URL}/api/reports/bell-curve`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setBellCurveData(res.data);
    } catch (err) {
      console.error('Failed to fetch bell curve:', err);
    } finally {
      setBellCurveLoading(false);
    }
  }, [startYear, startMonth, endYear, endMonth, bellCurveSubTeam, token]);

  useEffect(() => {
    fetchBellCurve();
  }, [fetchBellCurve]);

  // Bell curve download
  const downloadBellCurveReport = async () => {
    setDownloadingBellCurve(true);
    try {
      const params = {
        start_year: parseInt(startYear),
        start_month: parseInt(startMonth),
        end_year: parseInt(endYear),
        end_month: parseInt(endMonth)
      };
      if (bellCurveSubTeam !== 'all') params.sub_team = bellCurveSubTeam;
      const response = await axios.get(`${BACKEND_URL}/api/reports/bell-curve/excel`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filterLabel = bellCurveSubTeam !== 'all' ? bellCurveSubTeam : 'Marketing_Art';
      link.setAttribute('download', `Bell_Curve_${filterLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Bell curve download failed:', err);
      alert('Failed to download bell curve report');
    } finally {
      setDownloadingBellCurve(false);
    }
  };

  const downloadMemberReport = async (designer) => {
    setDownloadingId(designer.designer_id);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reports/member-performance/excel`, {
        params: {
          designer_id: designer.designer_id,
          start_year: parseInt(startYear),
          start_month: parseInt(startMonth),
          end_year: parseInt(endYear),
          end_month: parseInt(endMonth)
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${designer.name.replace(/\s+/g, '_')}_Performance.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download report');
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadAllReport = async () => {
    setDownloadingAll(true);
    try {
      // Calculate period from range
      const months = (parseInt(endYear) - parseInt(startYear)) * 12 + (parseInt(endMonth) - parseInt(startMonth)) + 1;
      const response = await axios.get(`${BACKEND_URL}/api/reports/team-performance/excel`, {
        params: { period: months },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Team_Performance_${months}_Months.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download all failed:', err);
      alert('Failed to download team report');
    } finally {
      setDownloadingAll(false);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => (
    sortBy === field ? (sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : null
  );

  const years = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(String(y));

  const periodLabel = `${FULL_MONTHS[parseInt(startMonth)-1]} ${startYear} — ${FULL_MONTHS[parseInt(endMonth)-1]} ${endYear}`;

  return (
    <div className="space-y-6" data-testid="period-scores-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")} data-testid="period-title">
            {defaultPeriod} Months Score
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            {periodLabel} &middot; {data.length} members
          </p>
        </div>
      </div>

      {/* Date Range + Filters Row */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Start */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>From</label>
              <div className="flex gap-1">
                <Select value={startMonth} onValueChange={setStartMonth}>
                  <SelectTrigger className="w-24" data-testid="start-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={startYear} onValueChange={setStartYear}>
                  <SelectTrigger className="w-20" data-testid="start-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <span className={cn("pb-2 text-sm", isDark ? "text-slate-500" : "text-slate-400")}>to</span>

            {/* End */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>To</label>
              <div className="flex gap-1">
                <Select value={endMonth} onValueChange={setEndMonth}>
                  <SelectTrigger className="w-24" data-testid="end-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={endYear} onValueChange={setEndYear}>
                  <SelectTrigger className="w-20" data-testid="end-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="h-px w-px" /> {/* spacer */}

            {/* Search */}
            <div className="flex-1 min-w-[180px]">
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Name or Employee ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className={cn(
                    "w-full pl-8 pr-3 py-2 rounded-md border text-sm",
                    isDark ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400" : "bg-white border-slate-200"
                  )}
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Team filter */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Sub-Team</label>
              <Select value={selectedTeam} onValueChange={(val) => { setSelectedTeam(val); setBellCurveSubTeam(val); }}>
                <SelectTrigger className="w-40" data-testid="team-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allTeams.map(t => <SelectItem key={t} value={t}>{t === 'all' ? 'All Sub-Teams' : t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Location filter */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Location</label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-32" data-testid="location-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {locations.map(l => <SelectItem key={l} value={l}>{l === 'all' ? 'All Locations' : l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Download All */}
            <Button
              variant="outline"
              className="gap-2"
              onClick={downloadAllReport}
              disabled={downloadingAll}
              data-testid="download-all-btn"
            >
              {downloadingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloadingAll ? 'Downloading...' : 'Download All'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sub-Team Breakdown */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className={cn("text-sm font-semibold flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            <Building2 className="w-4 h-4 text-[#331062]" />
            Sub-Team Breakdown
            <span className={cn("text-xs font-normal ml-1", isDark ? "text-slate-400" : "text-slate-500")}>({subTeamBreakdown.length} teams)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4 pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {subTeamBreakdown.map(([team, count]) => (
              <button
                key={team}
                onClick={() => { 
                  const newTeam = selectedTeam === team ? 'all' : team;
                  setSelectedTeam(newTeam); 
                  setBellCurveSubTeam(newTeam);
                }}
                className={cn(
                  "flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all cursor-pointer",
                  selectedTeam === team
                    ? "bg-[#331062] border-[#331062] text-white"
                    : isDark 
                      ? "bg-slate-700/50 border-slate-600 hover:border-[#331062] hover:bg-slate-700" 
                      : "bg-white border-slate-200 hover:border-[#331062] hover:bg-purple-50"
                )}
                data-testid={`subteam-card-${team}`}
              >
                <div className={cn(
                  "text-lg font-bold",
                  selectedTeam === team ? "text-white" : isDark ? "text-white" : "text-slate-800"
                )}>
                  {count}
                </div>
                <div className={cn(
                  "text-[10px] leading-tight mt-0.5",
                  selectedTeam === team ? "text-white/80" : isDark ? "text-slate-400" : "text-slate-500"
                )}>
                  {team}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bell Curve — Forced Distribution */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-5">
          {/* Header Row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#331062]" />
              <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                Bell Curve — Forced Distribution
              </h3>
              <span className={cn("text-xs px-2 py-0.5 rounded-full", isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-500")}>
                15% | 70% | 15%
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Sub-team filter for bell curve */}
              <Select value={bellCurveSubTeam} onValueChange={(val) => { setBellCurveSubTeam(val); setSelectedTeam(val); }}>
                <SelectTrigger className="w-52 h-9 text-sm" data-testid="bell-curve-subteam-filter"><SelectValue placeholder="Sub-Team" /></SelectTrigger>
                <SelectContent>
                  {bellCurveSubTeams.map(t => <SelectItem key={t} value={t}>{t === 'all' ? 'Whole Department' : t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-9"
                onClick={downloadBellCurveReport}
                disabled={downloadingBellCurve}
                data-testid="download-bell-curve-btn"
              >
                {downloadingBellCurve ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {downloadingBellCurve ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>

          {bellCurveLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#331062]" />
            </div>
          ) : bellCurveData ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Smooth Bell Curve SVG */}
              <div className="lg:col-span-2">
                <BellCurveSVG data={bellCurveData} isDark={isDark} />
              </div>

              {/* Summary Panel */}
              <div className="space-y-3">
                <div className={cn("text-center p-3 rounded-lg", isDark ? "bg-slate-700/50" : "bg-slate-50")}>
                  <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")} data-testid="bell-curve-total">
                    {bellCurveData.total}
                  </div>
                  <div className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                    {bellCurveSubTeam === 'all' ? 'Marketing Art (Whole Dept)' : bellCurveSubTeam}
                  </div>
                </div>

                {/* Top 15% */}
                <div className={cn("rounded-lg p-3 border", isDark ? "border-emerald-800 bg-emerald-900/20" : "border-emerald-200 bg-emerald-50")}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-emerald-600">Rating 4 & 5 (Top)</span>
                    <span className="text-xs text-emerald-600">Target: 15%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <span className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-800")}>{bellCurveData.actual?.top?.count || 0}</span>
                      <span className={cn("text-sm ml-1", isDark ? "text-slate-400" : "text-slate-500")}>members</span>
                    </div>
                    <span className={cn("text-lg font-bold", 
                      (bellCurveData.actual?.top?.pct || 0) > 20 ? "text-amber-500" : "text-emerald-600"
                    )}>
                      {bellCurveData.actual?.top?.pct || 0}%
                    </span>
                  </div>
                  <div className={cn("h-1.5 rounded-full mt-2 overflow-hidden", isDark ? "bg-slate-700" : "bg-emerald-100")}>
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(bellCurveData.actual?.top?.pct || 0, 100)}%` }} />
                  </div>
                </div>

                {/* Middle 70% */}
                <div className={cn("rounded-lg p-3 border", isDark ? "border-purple-800 bg-purple-900/20" : "border-purple-200 bg-purple-50")}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-[#331062]">Rating 3 (Middle)</span>
                    <span className="text-xs text-[#331062]">Target: 70%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <span className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-800")}>{bellCurveData.actual?.middle?.count || 0}</span>
                      <span className={cn("text-sm ml-1", isDark ? "text-slate-400" : "text-slate-500")}>members</span>
                    </div>
                    <span className={cn("text-lg font-bold",
                      Math.abs((bellCurveData.actual?.middle?.pct || 0) - 70) > 20 ? "text-amber-500" : "text-[#331062]"
                    )}>
                      {bellCurveData.actual?.middle?.pct || 0}%
                    </span>
                  </div>
                  <div className={cn("h-1.5 rounded-full mt-2 overflow-hidden", isDark ? "bg-slate-700" : "bg-purple-100")}>
                    <div className="h-full bg-[#331062] rounded-full transition-all" style={{ width: `${Math.min(bellCurveData.actual?.middle?.pct || 0, 100)}%` }} />
                  </div>
                </div>

                {/* Bottom 15% */}
                <div className={cn("rounded-lg p-3 border", isDark ? "border-red-800 bg-red-900/20" : "border-red-200 bg-red-50")}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-red-500">Rating 1 & 2 (Bottom)</span>
                    <span className="text-xs text-red-500">Target: 15%</span>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <span className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-800")}>{bellCurveData.actual?.bottom?.count || 0}</span>
                      <span className={cn("text-sm ml-1", isDark ? "text-slate-400" : "text-slate-500")}>members</span>
                    </div>
                    <span className={cn("text-lg font-bold",
                      (bellCurveData.actual?.bottom?.pct || 0) > 20 ? "text-red-500" : "text-red-400"
                    )}>
                      {bellCurveData.actual?.bottom?.pct || 0}%
                    </span>
                  </div>
                  <div className={cn("h-1.5 rounded-full mt-2 overflow-hidden", isDark ? "bg-slate-700" : "bg-red-100")}>
                    <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${Math.min(bellCurveData.actual?.bottom?.pct || 0, 100)}%` }} />
                  </div>
                </div>

                {/* Per-Rating Breakdown */}
                <div className={cn("rounded-lg p-3 border text-xs", isDark ? "border-slate-700 bg-slate-700/30" : "border-slate-200 bg-slate-50")}>
                  <div className="font-medium mb-2">Rating Breakdown</div>
                  {[5,4,3,2,1].map(r => {
                    const count = parseInt(bellCurveData.actual?.by_rating?.[String(r)] || 0);
                    const pct = bellCurveData.total > 0 ? ((count / bellCurveData.total) * 100).toFixed(1) : 0;
                    const colors = { 5: 'bg-emerald-500', 4: 'bg-blue-500', 3: 'bg-purple-500', 2: 'bg-amber-500', 1: 'bg-red-500' };
                    return (
                      <div key={r} className="flex items-center gap-2 py-0.5">
                        <div className={cn("w-5 h-5 rounded flex items-center justify-center text-white font-bold text-[10px]", colors[r])}>{r}</div>
                        <div className="flex-1">
                          <div className={cn("h-1.5 rounded-full overflow-hidden", isDark ? "bg-slate-600" : "bg-slate-200")}>
                            <div className={cn("h-full rounded-full", colors[r])} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <span className={cn("w-16 text-right", isDark ? "text-slate-400" : "text-slate-500")}>{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#331062]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Users className="w-10 h-10 text-slate-300 mb-3" />
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>No members found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead>
                  <tr className={cn("border-b", isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50")}>
                    <th className="text-left py-3 px-4 font-medium">#</th>
                    <th className="text-left py-3 px-4 font-medium">Member</th>
                    <th className="text-left py-3 px-4 font-medium">Team</th>
                    <th className={cn("py-3 px-3 font-medium cursor-pointer select-none text-center")}
                        onClick={() => toggleSort('avg_performance')}>
                      <span className="inline-flex items-center gap-1">Avg Perf <SortIcon field="avg_performance" /></span>
                    </th>
                    <th className={cn("py-3 px-3 font-medium cursor-pointer select-none text-center")}
                        onClick={() => toggleSort('avg_contribution')}>
                      <span className="inline-flex items-center gap-1">Avg Contrib <SortIcon field="avg_contribution" /></span>
                    </th>
                    <th className={cn("py-3 px-3 font-medium cursor-pointer select-none text-center")}
                        onClick={() => toggleSort('avg_ranking')}>
                      <span className="inline-flex items-center gap-1">Avg Ranking <SortIcon field="avg_ranking" /></span>
                    </th>
                    <th className="py-3 px-3 font-medium text-center">Weeks</th>
                    <th className="py-3 px-3 font-medium text-center">Level</th>
                    <th className="py-3 px-4 font-medium text-center">Report</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filtered.map((member, idx) => (
                      <motion.tr
                        key={member.designer_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={cn(
                          "border-b transition-colors",
                          isDark 
                            ? "border-slate-700 hover:bg-slate-700/50" 
                            : "border-slate-100 hover:bg-slate-50"
                        )}
                      >
                        <td className="py-3 px-4">
                          <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>{idx + 1}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#331062] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {member.name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-slate-800")}>{member.name}</p>
                              <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>{member.employee_id || member.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                            {member.sub_team || member.vertical_team || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn("font-semibold text-sm", getPerformanceScoreBg(member.avg_performance))}>
                            {member.avg_performance.toFixed(1)}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                            {member.avg_contribution.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn("font-bold text-sm", 
                            member.avg_ranking >= 4 ? "text-emerald-500" : 
                            member.avg_ranking >= 3 ? "text-blue-500" : 
                            member.avg_ranking >= 2 ? "text-amber-500" : "text-red-500"
                          )}>
                            {member.avg_ranking.toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                            {member.weeks_scored}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn(
                            "inline-block px-2 py-0.5 rounded-full text-xs font-medium",
                            member.performance_level === 'Outstanding' ? 'bg-emerald-100 text-emerald-700' :
                            member.performance_level === 'Strong Performer' ? 'bg-blue-100 text-blue-700' :
                            member.performance_level === 'Reliable Performer' ? 'bg-sky-100 text-sky-700' :
                            member.performance_level === 'Needs Improvement' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {member.performance_level}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => downloadMemberReport(member)}
                            disabled={downloadingId === member.designer_id}
                            data-testid={`download-member-${member.designer_id}`}
                          >
                            {downloadingId === member.designer_id 
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                            }
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PeriodScores;
